/**
 * Vincent XLSX 2026-07-13 21h Row 472 (Eric 2026-07-14 SPEC STRICK) —
 * Page « Accès aux parcours » (matrice utilisateurs × parcours).
 *
 * Accessible uniquement pour founder / admin / concepteur / employee_advanced.
 *
 * Structure :
 *   - Lignes = utilisateurs (tri par nom A→Z / Z→A ou par rôle)
 *   - Colonnes = parcours (séparés Intégration | Apprentissage)
 *   - Cases à cocher = accès individuel
 *   - Cases grisées = verrouillées (founder/admin cochés forcés, employee_new × learning décoché forcé)
 *   - Un rôle ne peut modifier que les cases des rôles STRICTEMENT INFÉRIEURS.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Users, Path } from "@phosphor-icons/react";
import { API_BASE as API } from "../lib/apiBase";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { getCanonicalLabel, roleLabelsFor } from "../lib/canonicalRoles";

// Tâche 1366 (Eric 2026-06-30) — Cette page portait sa PROPRE table de
// libellés de rôles, française en dur. Elle ignorait donc à la fois la langue
// de l'écran ET les noms que l'organisation a saisis dans « Gestion des
// rôles » : la matrice affichait « Employé formé » là où le reste de
// l'application affiche « Conseiller actif ». On passe par la source unique.

export default function PathwayAccessMatrixPage() {
  const { tr, lang } = useLang();
  const { user } = useAuth();
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("name_asc"); // name_asc | name_desc | role
  const [saving, setSaving] = useState({}); // { "userId::pathwayId": true }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/pathway-access/matrix`, { withCredentials: true });
      setMatrix(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Accès refusé ou erreur serveur", "Access denied or server error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const sortedUsers = useMemo(() => {
    if (!matrix) return [];
    const arr = [...matrix.users];
    if (sortBy === "name_asc") {
      arr.sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
    } else if (sortBy === "name_desc") {
      arr.sort((a, b) => (b.name || "").localeCompare(a.name || "", "fr", { sensitivity: "base" }));
    } else if (sortBy === "role") {
      arr.sort((a, b) => (b.role_weight || 0) - (a.role_weight || 0));
    }
    return arr;
  }, [matrix, sortBy]);

  // Index rapide des cellules par (userId, pathwayId)
  const cellsIndex = useMemo(() => {
    const idx = {};
    if (matrix) matrix.cells.forEach((c) => { idx[`${c.user_id}::${c.pathway_id}`] = c; });
    return idx;
  }, [matrix]);

  const toggleCell = async (userId, pathwayId, currentGranted) => {
    const key = `${userId}::${pathwayId}`;
    const cell = cellsIndex[key];
    if (!cell || !cell.editable) return;
    setSaving((s) => ({ ...s, [key]: true }));
    // Optimistic update
    setMatrix((m) => ({
      ...m,
      cells: m.cells.map((c) => c === cell ? { ...c, granted: !currentGranted, is_default: false } : c),
    }));
    try {
      await axios.post(`${API}/api/pathway-access/toggle`,
        { user_id: userId, pathway_id: pathwayId, granted: !currentGranted },
        { withCredentials: true }
      );
    } catch (e) {
      // Rollback
      setMatrix((m) => ({
        ...m,
        cells: m.cells.map((c) => c.user_id === userId && c.pathway_id === pathwayId
          ? { ...c, granted: currentGranted, is_default: cell.is_default } : c),
      }));
      toast.error(e?.response?.data?.detail || tr("Échec de la mise à jour", "Update failed"));
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  if (loading) return <div className="p-6 text-sm text-zinc-500">Chargement…</div>;
  if (!matrix) return <div className="p-6 text-sm text-rose-600">{tr("Accès refusé ou erreur.", "Access denied or error.")}</div>;

  const { onboarding_pathways, learning_pathways, weekly_pathways = [] } = matrix;
  // Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — Ajout du 3e type
  // « weekly » (Formations hebdomadaires) : nouvelle colonne dans la
  // matrice, avec bg jaune-ambre (distinct des bleus/violets des 2 autres).
  const allPathways = [...onboarding_pathways, ...learning_pathways, ...weekly_pathways];

  return (
    <div className="p-6 max-w-full" data-testid="pathway-access-matrix-page">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight font-['Outfit'] flex items-center gap-2">
            <Users size={26} className="text-[#002FA7]" />
            {tr("Accès aux parcours", "Pathway access")}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {tr(
              `${matrix.total_users} utilisateur(s) × ${matrix.total_pathways} parcours. Votre rôle :`,
              `${matrix.total_users} user(s) × ${matrix.total_pathways} pathways. Your role:`,
            )}{" "}
            <strong>{getCanonicalLabel(matrix.actor_role, roleLabelsFor(user, lang), false, lang) || matrix.actor_role}</strong>.{" "}
            {tr("Vous pouvez modifier uniquement les rôles inférieurs au vôtre.",
                "You can only change roles below your own.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-zinc-500">{tr("Trier par :", "Sort by:")}</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="h-9 px-2 border border-zinc-200 rounded-lg text-sm bg-white"
            data-testid="matrix-sort-select"
          >
            <option value="name_asc">{tr("Nom (A → Z)", "Name (A → Z)")}</option>
            <option value="name_desc">{tr("Nom (Z → A)", "Name (Z → A)")}</option>
            <option value="role">{tr("Rôle (privilège ↓)", "Role (privilege ↓)")}</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto border border-zinc-200 rounded-lg bg-white">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 z-10 bg-white">
            <tr>
              <th className="sticky left-0 bg-white z-20 px-3 py-2 text-left border-r border-zinc-200 min-w-[200px]">
                {tr("Utilisateur", "User")}
              </th>
              <th className="px-2 py-2 text-center border-r border-zinc-200 bg-zinc-50 min-w-[80px]">{tr("Rôle", "Role")}</th>
              {onboarding_pathways.length > 0 && (
                <th colSpan={onboarding_pathways.length}
                    className="px-2 py-2 text-center bg-blue-100 border-b-4 border-blue-500 text-blue-900 font-semibold">
                  <span className="inline-flex items-center gap-1"><Path size={14} /> {tr("Parcours d'intégration", "Onboarding pathways")}</span>
                </th>
              )}
              {learning_pathways.length > 0 && (
                <th colSpan={learning_pathways.length}
                    className="px-2 py-2 text-center bg-emerald-100 border-b-4 border-emerald-500 text-emerald-900 font-semibold">
                  <span className="inline-flex items-center gap-1"><Path size={14} /> {tr("Parcours d'apprentissage", "Learning pathways")}</span>
                </th>
              )}
              {weekly_pathways.length > 0 && (
                <th colSpan={weekly_pathways.length}
                    className="px-2 py-2 text-center bg-amber-100 border-b-4 border-amber-500 text-amber-900 font-semibold">
                  <span className="inline-flex items-center gap-1"><Path size={14} /> {tr("Formations hebdomadaires", "Weekly training")}</span>
                </th>
              )}
            </tr>
            <tr>
              <th className="sticky left-0 bg-white z-20 px-3 py-2 border-r border-zinc-200"></th>
              <th className="px-2 py-2 border-r border-zinc-200 bg-zinc-50"></th>
              {allPathways.map((p) => (
                <th key={p.id}
                    className={`px-2 py-2 text-[10px] font-medium border-b border-zinc-200 w-[140px] max-w-[140px] align-bottom ${p.pathway_type === "learning" ? "bg-emerald-50" : p.pathway_type === "weekly" ? "bg-amber-50" : "bg-blue-50"}`}
                    title={p.is_published ? p.name : `${p.name} — Brouillon (pas encore publié)`}
                    data-testid={`matrix-col-header-${p.id}`}>
                  {/* Vincent XLSX 2026-07-15 Row 482 (Eric 2026-07-16 SPEC STRICK 2e passe) —
                      Noms des parcours affichés EN ENTIER, wrap sur plusieurs lignes
                      sans "…". `whitespace-normal break-words` remplace `truncate`.
                      Vincent B650 (Eric 2026-08-05 SPEC STRICK PROD) — Badge
                      « Brouillon » sur les parcours non publiés pour que
                      l'utilisateur voie d'un coup d'œil que la case cochée
                      pré-configure l'accès avant publication. */}
                  <div className="whitespace-normal break-words leading-tight text-center">{p.name}</div>
                  {p.is_published === false && (
                    <div className="mt-0.5 text-center">
                      <span
                        className="inline-block px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded bg-zinc-200 text-zinc-600"
                        data-testid={`matrix-col-draft-badge-${p.id}`}
                      >
                        Brouillon
                      </span>
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((u, ri) => (
              <tr key={u.id} className={ri % 2 === 0 ? "bg-white" : "bg-zinc-50/40"} data-testid={`matrix-row-${u.id}`}>
                <td className="sticky left-0 bg-inherit px-3 py-2 border-r border-zinc-200 font-medium">
                  <div className="truncate max-w-[220px]" title={u.email}>{u.name}</div>
                  <div className="text-[10px] text-zinc-400 truncate">{u.email}</div>
                </td>
                <td className="px-2 py-2 text-center border-r border-zinc-200 text-[10px]">
                  <span className={`inline-block px-1.5 py-0.5 rounded font-semibold ${
                    u.role === "founder" || u.role === "admin" ? "bg-red-100 text-red-700"
                    : u.role === "concepteur" ? "bg-violet-100 text-violet-700"
                    : u.role === "employee_advanced" ? "bg-blue-100 text-blue-700"
                    : u.role === "employee_trained" ? "bg-teal-100 text-teal-700"
                    : "bg-zinc-100 text-zinc-600"
                  }`}>
                    {ROLE_LABEL[u.role] || u.role}
                  </span>
                </td>
                {allPathways.map((p) => {
                  const cell = cellsIndex[`${u.id}::${p.id}`];
                  if (!cell) return <td key={p.id} className="text-center">—</td>;
                  const key = `${u.id}::${p.id}`;
                  const isSaving = !!saving[key];
                  return (
                    <td key={p.id}
                        className={`px-2 py-2 text-center ${p.pathway_type === "learning" ? "bg-emerald-50/40" : p.pathway_type === "weekly" ? "bg-amber-50/40" : "bg-blue-50/40"}`}>
                      <input
                        type="checkbox"
                        checked={!!cell.granted}
                        disabled={!cell.editable || isSaving}
                        onChange={() => toggleCell(u.id, p.id, cell.granted)}
                        className={`h-4 w-4 accent-[#002FA7] ${!cell.editable ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                        data-testid={`matrix-cell-${u.id}-${p.id}`}
                        title={
                          !cell.editable
                            ? (cell.is_locked ? "Verrouillé (règle Vincent)" : "Vous ne pouvez pas modifier un rôle égal ou supérieur au vôtre")
                            : (cell.granted ? "Cliquer pour retirer l'accès" : "Cliquer pour donner l'accès")
                        }
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-zinc-500 space-y-2">
        {/* Vincent B662 (Eric 2026-08-05 SPEC STRICK PROD) — Légende visuelle
            des 3 catégories de parcours pour éviter toute ambiguïté couleur. */}
        <div className="flex flex-wrap items-center gap-3">
          <strong>{tr("Légende :", "Legend:")}</strong>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-blue-100 border border-blue-500" />
            <span className="text-zinc-700">{tr("Parcours d'intégration", "Onboarding pathways")}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-500" />
            <span className="text-zinc-700">{tr("Parcours d'apprentissage", "Learning pathways")}</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-amber-100 border border-amber-500" />
            <span className="text-zinc-700">{tr("Formations hebdomadaires", "Weekly training")}</span>
          </span>
        </div>
        <div><strong>{tr("Règles d'accès par défaut :", "Default access rules:")}</strong></div>
        <ul className="list-disc pl-6">
          <li>{tr("Fondateur / Admin : accès à TOUS les parcours, cases verrouillées", "Founder / Admin: access to EVERY pathway, boxes locked")}</li>
          <li>{tr("Concepteur / Employé avancé : accès à TOUS les parcours par défaut, cases décochables", "Designer / Advanced employee: access to EVERY pathway by default, boxes can be unticked")}</li>
          <li>{tr("Employé formé : accès aux parcours d'intégration par défaut, apprentissage à attribuer manuellement", "Trained employee: onboarding pathways by default, learning assigned by hand")}</li>
          <li>{tr("Nouvel employé : accès aux parcours d'intégration UNIQUEMENT (apprentissage grisé)", "New employee: onboarding pathways ONLY (learning greyed out)")}</li>
        </ul>
      </div>
    </div>
  );
}
